import React, { useCallback, useMemo, useState } from 'react';
import {
    Cell,
    Column,
    ColumnResizer,
    ResizableTableContainer,
    Row,
    Table,
    TableBody,
    TableHeader,
    type SortDescriptor
} from 'react-aria-components';
import toast from 'react-hot-toast';
import type { IntegrationDto, CreateIntegrationRequest, UpdateIntegrationRequest, User } from '../../types';
import { IntegrationEditModal, IntegrationInfoModal, IntegrationSchedulerModal } from '../IntegrationModal';
import CategorySelectionModal from '../IntegrationModal/CategorySelectionModal';
import { useGridLocalStorageSettings } from '../../hooks/useGridLocalStorageSettings';
import { useIntegrations } from '../../hooks/useIntegrations';
import apiClient from '../../shared/api/apiClient';
import './styles.css';

interface IntegrationManagementProps {
    onIntegrationSelect?: (integration: IntegrationDto) => void;
    currentUser: User | null;
}

type IntegrationSortField =
    | 'id'
    | 'name'
    | 'supplierName'
    | 'lastSynchronization'
    | 'averageExecutionTimeSeconds'
    | 'productCount'
    | 'jobCount';

type IntegrationColumnKey = IntegrationSortField | 'actions';
type IntegrationGridFilters = Record<string, never>;

const integrationColumnOrder: IntegrationColumnKey[] = [
    'id',
    'name',
    'supplierName',
    'lastSynchronization',
    'averageExecutionTimeSeconds',
    'productCount',
    'jobCount',
    'actions'
];

const integrationColumnInitialWidths: Record<IntegrationColumnKey, number> = {
    id: 40,
    name: 180,
    supplierName: 180,
    lastSynchronization: 210,
    averageExecutionTimeSeconds: 190,
    productCount: 110,
    jobCount: 95,
    actions: 200
};

const integrationColumnMinWidths: Record<IntegrationColumnKey, number> = {
    id: 16,
    name: 16,
    supplierName: 16,
    lastSynchronization: 16,
    averageExecutionTimeSeconds: 16,
    productCount: 16,
    jobCount: 16,
    actions: 188
};

const integrationGridDefaultSort: SortDescriptor = {
    column: 'id',
    direction: 'ascending'
};

const integrationGridDefaultFilters: IntegrationGridFilters = {};

const mapResizedColumnWidths = (
    nextWidths: Map<unknown, unknown>,
    currentWidths: Record<IntegrationColumnKey, number>
): Record<IntegrationColumnKey, number> => {
    const normalizedWidths = { ...currentWidths };

    for (const key of integrationColumnOrder) {
        const width = nextWidths.get(key);

        if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
            normalizedWidths[key] = width;
        }
    }

    return normalizedWidths;
};

const isIntegrationSortField = (column: unknown): column is IntegrationSortField => {
    return column === 'id'
        || column === 'name'
        || column === 'supplierName'
        || column === 'lastSynchronization'
        || column === 'averageExecutionTimeSeconds'
        || column === 'productCount'
        || column === 'jobCount';
};

const isIntegrationSortDescriptor = (value: unknown): value is SortDescriptor => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const descriptor = value as Partial<SortDescriptor>;

    return isIntegrationSortField(descriptor.column)
        && (descriptor.direction === 'ascending' || descriptor.direction === 'descending');
};

const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Никогда';
    return new Date(dateString).toLocaleString('ru-RU');
};

const formatLastSynchronization = (integration: IntegrationDto) => {
    const lastPullRun = integration.lastPullRun;

    if (!lastPullRun) {
        return { text: 'никогда', isError: false, isClickable: false };
    }

    if (lastPullRun.endTime === null) {
        return { text: 'в процессе', isError: false, isClickable: false };
    }

    if (!lastPullRun.success) {
        return { text: 'ошибка', isError: true, isClickable: true };
    }

    return {
        text: formatDate(lastPullRun.startTime),
        isError: false,
        isClickable: true
    };
};

const formatAvgExecutionTime = (seconds: number | null) => {
    if (!seconds || seconds === 0) return '—';
    return seconds.toFixed(3) + 'с';
};

const sortIntegrations = (integrations: IntegrationDto[], sortDescriptor: SortDescriptor) => {
    const sortField = isIntegrationSortField(sortDescriptor.column)
        ? sortDescriptor.column
        : 'id';
    const isAscending = sortDescriptor.direction === 'ascending';

    return [...integrations].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortField) {
            case 'id':
                aValue = a.id;
                bValue = b.id;
                break;
            case 'name':
                aValue = a.name.toLowerCase();
                bValue = b.name.toLowerCase();
                break;
            case 'supplierName':
                aValue = a.supplierName.toLowerCase();
                bValue = b.supplierName.toLowerCase();
                break;
            case 'lastSynchronization':
                aValue = a.lastPullRun ? Date.parse(a.lastPullRun.startTime) || 0 : 0;
                bValue = b.lastPullRun ? Date.parse(b.lastPullRun.startTime) || 0 : 0;
                break;
            case 'averageExecutionTimeSeconds':
                aValue = a.averageExecutionTimeSeconds ?? 0;
                bValue = b.averageExecutionTimeSeconds ?? 0;
                break;
            case 'productCount':
                aValue = a.productCount ?? 0;
                bValue = b.productCount ?? 0;
                break;
            case 'jobCount':
                aValue = a.jobCount ?? 0;
                bValue = b.jobCount ?? 0;
                break;
            default:
                aValue = a.id;
                bValue = b.id;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.localeCompare(bValue);
            return isAscending ? comparison : -comparison;
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return isAscending ? aValue - bValue : bValue - aValue;
        }

        return 0;
    });
};

const IntegrationManagement: React.FC<IntegrationManagementProps> = ({ currentUser }) => {
    const {
        integrations,
        loading,
        loadIntegrations,
        addIntegration,
        updateIntegration,
        deleteIntegration,
    } = useIntegrations();

    const [selectedIntegration, setSelectedIntegration] = useState<IntegrationDto | null>(null);
    const [modalType, setModalType] = useState<'edit' | 'info' | 'scheduler' | null>(null);
    const [categorySelectionModalOpen, setCategorySelectionModalOpen] = useState(false);
    const {
        columnWidths: savedColumnWidths,
        setColumnWidths: setSavedColumnWidths,
        setSort: setSavedSort,
        sort: sortDescriptor
    } = useGridLocalStorageSettings<IntegrationColumnKey, SortDescriptor, IntegrationGridFilters>({
        defaultColumnWidths: integrationColumnInitialWidths,
        defaultFilters: integrationGridDefaultFilters,
        defaultSort: integrationGridDefaultSort,
        storageKey: 'gridSettings.integrations',
        validateSort: isIntegrationSortDescriptor,
        version: 1
    });

    const handleColumnResizeEnd = useCallback((nextWidths: Map<unknown, unknown>) => {
        setSavedColumnWidths(mapResizedColumnWidths(nextWidths, savedColumnWidths));
    }, [savedColumnWidths, setSavedColumnWidths]);

    // Обработчики модальных окон
    const handleCreateIntegration = useCallback(() => {
        setSelectedIntegration(null);
        setModalType('edit');
    }, []);

    const handleEditIntegration = useCallback((integration: IntegrationDto) => {
        setSelectedIntegration(integration);
        setModalType('edit');
    }, []);

    const handleViewIntegration = useCallback((integration: IntegrationDto) => {
        setSelectedIntegration(integration);
        setModalType('info');
    }, []);

    const handleSchedulerSettings = useCallback((integration: IntegrationDto) => {
        setSelectedIntegration(integration);
        setModalType('scheduler');
    }, []);

    const handleCategorySelection = useCallback((integration: IntegrationDto) => {
        setSelectedIntegration(integration);
        setCategorySelectionModalOpen(true);
    }, []);

    const handleRunNowIntegration = useCallback(async (integration: IntegrationDto) => {
        if (window.confirm(`Вы уверены, что хотите запустить интеграцию "${integration.name}" сейчас?`)) {
            try {
                await apiClient.post(`/api/Integrations/${integration.id}/run`);

                toast.success('Интеграция запущена успешно!');
                // Optionally reload integrations to update last pull date
                setTimeout(() => {
                    loadIntegrations();
                }, 1000);
            } catch (error) {
                if (error instanceof Error && 'statusCode' in error && error.statusCode === 409) {
                    toast.error('Интеграция уже выполняется');
                    return;
                }
                console.error('Ошибка при запуске интеграции:', error);
                toast.error('Ошибка при запуске интеграции: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
            }
        }
    }, [loadIntegrations]);

    const handleDeleteIntegration = useCallback(async (integration: IntegrationDto) => {
        if (window.confirm(`Вы уверены, что хотите удалить интеграцию "${integration.name}"?`)) {
            try {
                await apiClient.delete(`/api/Integrations/${integration.id}`);

                deleteIntegration(integration.id);
                toast.success('Интеграция удалена успешно!');
            } catch (error) {
                console.error('Ошибка при удалении интеграции:', error);
                toast.error('Ошибка при удалении интеграции: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
            }
        }
    }, [deleteIntegration]);

    const handleModalClose = useCallback(() => {
        setModalType(null);
        setSelectedIntegration(null);
        setTimeout(() => {
            loadIntegrations();
        });
    }, [loadIntegrations]);

    const handleSaveIntegration = useCallback(async (integrationData: CreateIntegrationRequest | UpdateIntegrationRequest) => {
        try {
            if (selectedIntegration) {
                await apiClient.put(`/api/Integrations/${selectedIntegration.id}`, integrationData);

                // Сохраняем дни отгрузки, если они есть
                const shippingDaysData = 'shippingDays' in integrationData ? integrationData.shippingDays : undefined;
                if (shippingDaysData && Array.isArray(shippingDaysData) && shippingDaysData.length > 0) {
                    await apiClient.put(`/api/Integrations/${selectedIntegration.id}/shipping-days`, {
                        shippingDays: shippingDaysData.map(sd => ({
                            id: sd.id,
                            dayOfWeek: sd.dayOfWeek,
                            cutoffTime: sd.cutoffTime || '00:00:00'
                        }))
                    });
                }

                // Обновляем локальное состояние
                updateIntegration(selectedIntegration.id, integrationData);

                toast.success('Интеграция обновлена успешно!');
            } else {
                // Создание новой интеграции
                const result = await apiClient.post<IntegrationDto>('/api/Integrations', integrationData);
                addIntegration(result.data);
                toast.success('Интеграция создана успешно!');
            }
            handleModalClose();
        } catch (error) {
            console.error('Ошибка при сохранении интеграции:', error);
            toast.error('Ошибка при сохранении интеграции: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        }
    }, [addIntegration, handleModalClose, selectedIntegration, updateIntegration]);

    const getSortIndicator = (field: IntegrationSortField) => {
        if (sortDescriptor.column === field) {
            return sortDescriptor.direction === 'ascending' ? '↑' : '↓';
        }
        return '';
    };

    const handleSortChange = useCallback((descriptor: SortDescriptor) => {
        if (!isIntegrationSortField(descriptor.column)) {
            return;
        }

        setSavedSort(descriptor);
    }, [setSavedSort]);

    const sortedIntegrations = useMemo(
        () => sortIntegrations(integrations, sortDescriptor),
        [integrations, sortDescriptor]
    );

    const integrationRows = useMemo(() => sortedIntegrations.map(integration => {
        const lastSync = formatLastSynchronization(integration);

        return (
            <Row key={integration.id} id={integration.id}>
                <Cell>{integration.id}</Cell>
                <Cell>{integration.name}</Cell>
                <Cell>{integration.supplierName}</Cell>
                <Cell>
                    <span
                        className={lastSync.isClickable ? 'clickable-link' : ''}
                        onClick={lastSync.isClickable ? () => handleViewIntegration(integration) : undefined}
                        style={{
                            cursor: lastSync.isClickable ? 'pointer' : 'default',
                            color: lastSync.isError ? '#d32f2f' : lastSync.isClickable ? '#007bff' : 'inherit',
                            textDecoration: lastSync.isClickable ? 'underline' : 'none'
                        }}
                        title={lastSync.isClickable ? 'Нажмите для просмотра истории запусков' : ''}
                    >
                        {lastSync.text}
                    </span>
                </Cell>
                <Cell>{formatAvgExecutionTime(integration.averageExecutionTimeSeconds)}</Cell>
                <Cell>{integration.productCount || 0}</Cell>
                <Cell>{integration.jobCount || 0}</Cell>
                <Cell className="actions-column">
                    <div className="action-buttons">
                        <button
                            className="btn btn-small btn-info"
                            onClick={() => handleViewIntegration(integration)}
                            title="Просмотреть информацию"
                        >
                            👁
                        </button>
                        <button
                            className="btn btn-small btn-edit"
                            onClick={() => handleEditIntegration(integration)}
                            title="Редактировать"
                        >
                            ✏️
                        </button>
                        <button
                            className="btn btn-small btn-categories"
                            onClick={() => handleCategorySelection(integration)}
                            title="Выбрать категории"
                        >
                            🏷️
                        </button>
                        <button
                            className="btn btn-small btn-scheduler"
                            onClick={() => handleSchedulerSettings(integration)}
                            title="Настройки планировщика"
                        >
                            ⏰
                        </button>
                        <button
                            className="btn btn-small btn-run-now"
                            onClick={() => handleRunNowIntegration(integration)}
                            title="Запустить сейчас"
                        >
                            ▶️
                        </button>
                        <button
                            className="btn btn-small btn-delete"
                            onClick={() => handleDeleteIntegration(integration)}
                            title="Удалить"
                        >
                            ✕
                        </button>
                    </div>
                </Cell>
            </Row>
        );
    }), [
        sortedIntegrations,
        handleViewIntegration,
        handleEditIntegration,
        handleCategorySelection,
        handleSchedulerSettings,
        handleRunNowIntegration,
        handleDeleteIntegration
    ]);

    return (
        <div className="user-management container-fluid">
            {/* Compact Filter Controls */}
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Интеграции</h3>

                    <div className="filter-controls-right">
                        <button
                            className="btn-compact refresh"
                            onClick={() => loadIntegrations()}
                            disabled={loading}
                            title="Обновить"
                        >
                            {loading ? (
                                <div className="btn-spinner" />
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="23,4 23,10 17,10"/>
                                    <polyline points="1,20 1,14 7,14"/>
                                    <path d="M20.49,9A9,9,0,0,0,5.64,5.64L1,10m22,4l-4.64,4.36A9,9,0,0,1,3.51,15"/>
                                </svg>
                            )}
                        </button>

                        <button
                            className="btn-compact primary"
                            onClick={handleCreateIntegration}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14"/>
                            </svg>
                            <span>Создать</span>
                        </button>
                    </div>
                </div>
            </div>

            {(
                <div className="integration-management-table-container">
                    <ResizableTableContainer
                        className="integration-management-resizable-container"
                        onResizeEnd={handleColumnResizeEnd}
                    >
                        <Table
                            aria-label="Интеграции"
                            className="integration-management-table"
                            sortDescriptor={sortDescriptor}
                            onSortChange={handleSortChange}
                        >
                            <TableHeader>
                                <Column id="id" allowsSorting className="sortable-header" isRowHeader defaultWidth={savedColumnWidths.id} minWidth={integrationColumnMinWidths.id}>
                                    <span className="column-title">ID {getSortIndicator('id') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки ID" />
                                </Column>
                                <Column id="name" allowsSorting className="sortable-header" defaultWidth={savedColumnWidths.name} minWidth={integrationColumnMinWidths.name}>
                                    <span className="column-title">Название {getSortIndicator('name') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки Название" />
                                </Column>
                                <Column id="supplierName" allowsSorting className="sortable-header" defaultWidth={savedColumnWidths.supplierName} minWidth={integrationColumnMinWidths.supplierName}>
                                    <span className="column-title">Поставщик {getSortIndicator('supplierName') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки Поставщик" />
                                </Column>
                                <Column id="lastSynchronization" allowsSorting className="sortable-header" defaultWidth={savedColumnWidths.lastSynchronization} minWidth={integrationColumnMinWidths.lastSynchronization}>
                                    <span className="column-title">Последняя синхронизация {getSortIndicator('lastSynchronization') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки Последняя синхронизация" />
                                </Column>
                                <Column id="averageExecutionTimeSeconds" allowsSorting className="sortable-header" defaultWidth={savedColumnWidths.averageExecutionTimeSeconds} minWidth={integrationColumnMinWidths.averageExecutionTimeSeconds}>
                                    <span className="column-title">Среднее время выполнения {getSortIndicator('averageExecutionTimeSeconds') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки Среднее время выполнения" />
                                </Column>
                                <Column id="productCount" allowsSorting className="sortable-header" defaultWidth={savedColumnWidths.productCount} minWidth={integrationColumnMinWidths.productCount}>
                                    <span className="column-title">Продукты {getSortIndicator('productCount') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки Продукты" />
                                </Column>
                                <Column id="jobCount" allowsSorting className="sortable-header" defaultWidth={savedColumnWidths.jobCount} minWidth={integrationColumnMinWidths.jobCount}>
                                    <span className="column-title">Задачи {getSortIndicator('jobCount') || '\u00A0'}</span>
                                    <ColumnResizer className="column-resizer" aria-label="Изменить ширину колонки Задачи" />
                                </Column>
                                <Column id="actions" className="actions-column" defaultWidth={savedColumnWidths.actions} minWidth={integrationColumnMinWidths.actions} />
                            </TableHeader>
                            <TableBody>{integrationRows}</TableBody>
                        </Table>
                    </ResizableTableContainer>
                </div>
            )}

            {/* Модальные окна */}
            {modalType === 'edit' && (
                <IntegrationEditModal
                    integration={selectedIntegration}
                    currentUser={currentUser}
                    isOpen={true}
                    onClose={handleModalClose}
                    onSave={handleSaveIntegration}
                />
            )}

            {modalType === 'info' && (
                <IntegrationInfoModal
                    integration={selectedIntegration}
                    isOpen={true}
                    onClose={handleModalClose}
                />
            )}

            {modalType === 'scheduler' && (
                <IntegrationSchedulerModal
                    integration={selectedIntegration}
                    isOpen={true}
                    onClose={handleModalClose}
                    onJobDeleted={() => loadIntegrations()}
                />
            )}

            <CategorySelectionModal
                integration={selectedIntegration}
                isOpen={categorySelectionModalOpen}
                onClose={() => setCategorySelectionModalOpen(false)}
                currentUser={currentUser}
            />
        </div>
    );
};

export default IntegrationManagement;
